/**
 * 左侧字段面板：把当前表的字段直接摆出来，点一下就插进画布。
 *
 * 以前左栏只有一列「+ 标题/信息网格/表格…」的区块按钮 —— 那是「我要放个什么
 * 形状」，而用户脑子里想的是「我要把客户名称印上去」。中间这层转换得他自己做：
 * 先加一个区块，再进属性面板翻字段下拉。竞品左栏列的就是字段，点一下直接落到
 * 画布上，少两步。
 *
 * 关联字段展开成一组**子表字段**：画布上选中一个格子再点，那一列就绑上了；
 * 不选格子点「插入整张明细表」，表头 + 列一次带全。以前配明细要在右侧属性栏
 * 一列一列翻下拉，用户根本找不到入口。
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Tooltip, Typography } from 'antd';
import { RightOutlined, DownOutlined, TableOutlined } from '@ant-design/icons';
import type { FieldMeta } from '../api/types';
import { LINK_FIELD_TYPES, linkTargets, loadFieldMetas } from '../contract/linkedMeta';

/** 字段类型 → 一个能一眼区分的小图标。数字用 #，日期用日历，等等。 */
function typeIcon(type: number): string {
  switch (type) {
    case 2: case 99003: case 99002: case 99004: return '#';
    case 5: case 1001: case 1002: return '📅';
    case 11: case 1003: case 1004: return '👤';
    case 15: return '🔗';
    case 17: return '📎';
    case 18: case 21: return '⇄';
    case 7: return '☑';
    case 3: case 4: return '▾';
    case 13: return '📞';
    case 99005: return '✉';
    case 20: case 19: return 'ƒ';
    default: return 'A';
  }
}

export interface FieldPaletteProps {
  fieldMetas: FieldMeta[];
  /** 插入一个普通字段（写成 {{字段名}}）。 */
  onInsertField: (fieldName: string) => void;
  /** 插入一张绑定了该关联字段的动态明细表格，列按子表字段预填。 */
  onInsertLinkTable: (field: FieldMeta, subFields: FieldMeta[]) => void;
  /** 点了某个子表字段：选中的格子所在列绑上它；没选格子就插一张只有这一列的明细表。 */
  onInsertLinkField: (link: FieldMeta, sub: FieldMeta) => void;
  /** 画布上是否正选着格子（决定提示文案）。 */
  hasCellSelection?: boolean;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  padding: '4px 6px',
  marginBottom: 2,
  borderRadius: 5,
  fontSize: 12,
  color: 'var(--ink)',
  textAlign: 'left',
  cursor: 'pointer',
};

function FieldRow({
  field,
  onClick,
  tip,
  indent,
  trailing,
}: {
  field: FieldMeta;
  onClick: () => void;
  tip: string;
  indent?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <Tooltip title={tip} placement="right">
      <button
        className="pp-bare-button"
        onClick={onClick}
        style={{ ...rowStyle, paddingLeft: indent ? 22 : 6 }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <span style={{ width: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 11, flexShrink: 0 }}>
          {typeIcon(field.type)}
        </span>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {field.name}
        </span>
        {trailing}
      </button>
    </Tooltip>
  );
}

export function FieldPalette({
  fieldMetas,
  onInsertField,
  onInsertLinkTable,
  onInsertLinkField,
  hasCellSelection,
}: FieldPaletteProps) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  /** tableId → 子表字段。侧边栏里问 SDK，独立页里来自快照。 */
  const [subFields, setSubFields] = useState<Record<string, FieldMeta[]>>({});

  const targets = useMemo(() => linkTargets(fieldMetas), [fieldMetas]);
  useEffect(() => {
    let cancelled = false;
    for (const { tableId } of targets) {
      if (subFields[tableId]) continue;
      loadFieldMetas(tableId)
        .then((metas) => {
          if (!cancelled) setSubFields((m) => ({ ...m, [tableId]: metas }));
        })
        .catch(() => {
          if (!cancelled) setSubFields((m) => ({ ...m, [tableId]: [] }));
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets]);

  const kw = q.trim().toLowerCase();
  const list = useMemo(
    () => (kw ? fieldMetas.filter((f) => f.name.toLowerCase().includes(kw)) : fieldMetas),
    [fieldMetas, kw],
  );

  return (
    <div style={{ marginBottom: 14 }}>
      <div className="pp-panel-title">字段（当前表）</div>
      <Typography.Paragraph type="secondary" style={{ fontSize: 10.5, margin: '0 0 6px', lineHeight: 1.6 }}>
        点字段插进画布。关联字段可展开，选中表格格子再点子表字段，那一列就绑上。
      </Typography.Paragraph>
      <Input size="small" allowClear placeholder="搜索字段" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 6 }} />
      {fieldMetas.length === 0 && (
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          还没读到字段
        </Typography.Text>
      )}
      {list.length === 0 && fieldMetas.length > 0 && (
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          没有匹配的字段
        </Typography.Text>
      )}
      <div style={{ maxHeight: 320, overflowY: 'auto' }} className="pp-scroll">
        {list.map((f) => {
          const isLink = LINK_FIELD_TYPES.includes(f.type);
          if (!isLink) {
            return <FieldRow key={f.id} field={f} tip={`插入 {{${f.name}}}`} onClick={() => onInsertField(f.name)} />;
          }
          const tableId = targets.find((t) => t.field.id === f.id)?.tableId ?? '';
          const subs = subFields[tableId];
          const expanded = open[f.id] ?? !!kw;
          const shownSubs = (subs ?? []).filter((s) => !kw || s.name.toLowerCase().includes(kw));
          return (
            <div key={f.id}>
              <FieldRow
                field={f}
                tip="关联字段：展开看子表字段；「插入整张明细表」一次带全表头和列"
                onClick={() => setOpen((o) => ({ ...o, [f.id]: !expanded }))}
                trailing={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: 'var(--accent)' }}>明细</span>
                    {expanded ? <DownOutlined style={{ fontSize: 9, color: 'var(--ink-3)' }} /> : <RightOutlined style={{ fontSize: 9, color: 'var(--ink-3)' }} />}
                  </span>
                }
              />
              {expanded && (
                <div style={{ borderLeft: '2px solid var(--border-strong)', marginLeft: 13, marginBottom: 4 }}>
                  <Button
                    size="small"
                    type="dashed"
                    icon={<TableOutlined />}
                    style={{ margin: '2px 0 4px 8px', fontSize: 11.5 }}
                    onClick={() => onInsertLinkTable(f, subs ?? [])}
                  >
                    插入整张明细表
                  </Button>
                  {subs === undefined && (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', paddingLeft: 10 }}>正在读取子表字段…</div>
                  )}
                  {subs && subs.length === 0 && (
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', paddingLeft: 10 }}>读不到子表字段</div>
                  )}
                  {shownSubs.map((s) => (
                    <FieldRow
                      key={s.id}
                      field={s}
                      indent
                      tip={
                        hasCellSelection
                          ? `把选中格子所在的列绑到「${s.name}」`
                          : `插一张明细表，第一列取「${s.name}」；先选中一个格子再点则只绑那一列`
                      }
                      onClick={() => onInsertLinkField(f, s)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
